import { EventName, FormHeading } from "@/interface/types";
import { handleCampaignID } from "@/lib/utils";
import { E164Number } from "libphonenumber-js/core";
import React, { FormEvent, useEffect, useState } from "react";
import PhoneInputWithCountrySelect, {
  getCountryCallingCode,
} from "react-phone-number-input";
import Image from "next/image";
import { captureAndStoreQueryParamsBase64 } from "@/lib/queryParams";
import {
  useWebinarSchedule,
  useWebinarScheduleLoading,
} from "@/contexts/EventContext";
import { getWebinarEntry, getOrdinalDateParts } from "@/data/webinarSchedule";
import WebinarDateSkeleton from "@/components/common/WebinarDateSkeleton";
interface OptionType {
  value: number | string | null;
  label: string;
}

// Custom component to display country code instead of flag.
// Factory wraps so the country-code text picks up the same color as the
// surrounding inputs based on the form's dark/light background.
const makeCountryCodeDisplay = (darkBackground: boolean) => {
  const Component = ({ country }: { country: string }) => {
    const callingCode = country ? getCountryCallingCode(country as any) : "";
    return (
      <span
        className={`inline-block min-w-[30px] text-sm font-medium ${
          darkBackground ? "text-[#FFF]" : "text-[#595858]"
        }`}
      >
        +{callingCode}
      </span>
    );
  };
  Component.displayName = "CountryCodeDisplay";
  return Component;
};

const CairaEventForm: React.FC<{
  eventName: string;
  courseName?: string;
  formHeading: string;
  eventDate: string;
  eventTime: string;
  submitButtonName: string;
  courseId?: number | "";
  cityName?: string;
  cityId?: string | number;
  isOfflineFrom?: boolean;
  eventId?: string;
  formType?: string;
  orientationId?: string | number;
  isDarkBackground?: boolean;
  eventType?: string;
}> = ({
  eventName,
  formHeading,
  eventDate,
  eventTime,
  submitButtonName,
  courseId,
  courseName,
  cityName = "Virtual Event",
  cityId,
  isOfflineFrom = false,
  eventId,
  orientationId,
  isDarkBackground,
  formType,
  eventType,
}) => {
  const webinarSchedule = useWebinarSchedule();
  const isScheduleLoading = useWebinarScheduleLoading();
  const weeklyWebinarEntry = getWebinarEntry("weekly", webinarSchedule);
  const weeklyDateParts = getOrdinalDateParts(weeklyWebinarEntry.date);

  const initialState = {
    name: "",
    phone: "",
    email: "",
    course_id: courseId || "",
    whatsappAllow: 1,
    location: cityName,
    locationId: cityId || "VirtualEvent",
    corporateName: { value: null, label: "" } as OptionType,
    universityName: { value: null, label: "" } as OptionType,
    courseName: courseName || "",
    educational_qualification: "",
    iAmHereAs: "",
  };

  const buttonInitialData = {
    text: submitButtonName || "Submit",
    disabled: false,
    show: true,
  };

  const [data, setData] = useState(initialState);
  const [formButtonText, setFormButtonText] = useState(buttonInitialData);
  const [step, setStep] = useState<number>(1);
  const [tkId, setTkId] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);

  // Memoize so PhoneInput keeps a stable flagComponent reference across renders.
  const CountryCodeDisplay = React.useMemo(
    () => makeCountryCodeDisplay(!!isDarkBackground),
    [isDarkBackground],
  );

  const handlePhoneChange = (value?: E164Number | undefined) => {
    setPhoneError(false);
    setData((prevData) => ({
      ...prevData,
      phone: value ?? "",
    }));
  };

  const handleInputChange = (e: FormEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.currentTarget;

    if (name === "name") setNameError(false);
    if (name === "email") setEmailError(false);

    setData((prev) => ({
      ...prev,
      [name]: name === "whatsappAllow" ? (checked ? 1 : 0) : value,
    }));
  };

  const validateForm = () => {
    let isValid = true;

    if (!data.name.trim() || data.name.trim().length < 2) {
      setNameError(true);
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.email.trim() || !emailRegex.test(data.email)) {
      setEmailError(true);
      isValid = false;
    }

    if (!data.phone) {
      setPhoneError(true);
      isValid = false;
    }

    return isValid;
  };

  const validateOfflineForm = () => {
    if (!validateForm()) {
      return false;
    }

    if (data.iAmHereAs === "corporate" && !data.corporateName.value) {
      return false;
    }
    if (data.iAmHereAs === "university" && !data.universityName.value) {
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isOfflineFrom && !validateOfflineForm()) {
      return;
    } else if (!validateForm()) {
      return;
    }

    const payload = {
      first_name: data.name,
      phone: data.phone || null,
      email: data.email,
      city: cityName,
      city_id: cityId,
      whatsapp_opt_in: data.whatsappAllow === 1,
      campaign_tk: tkId || "e3a268cf-420c-415c-a234-2825d878e688",
      whatsappAllow: 1,
      smsAllow: 1,
      contact_number: data.phone || null,
      course_id: data.course_id || null,
      course_name: data.courseName || null,
      type: data.iAmHereAs || null,
      interested_event_id: eventId || null,
      register_for_event: eventType || null,
      eventName: eventName || null,
      register_for_webinar: true,
      coming_from: `website-${formType}`,
      page_full_url: window.location.href,
      url_page_path: window.location.pathname,
      query_params_base64: captureAndStoreQueryParamsBase64() ?? "",
    };

    if (isOfflineFrom) {
      Object.assign(payload, {
        interested_to_work_in_us: true,
        education_qualification: 1,
        large_event_registration_info: {
          type: data.iAmHereAs === "enrolled" ? "enrolled" : "not_enrolled",
          orientation_id: orientationId || null,
        },
      });
    }

    try {
      setFormButtonText({
        text: "Submitting...",
        disabled: true,
        show: true,
      });
      const response = await fetch(
        isOfflineFrom
          ? "/api/eventCms/offlineForm"
          : "/api/eventCms/onlineForm",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        return;
      }

      await response.json();
      setStep(2);
    } catch (error) {
      console.error("Network Error:", error);
    } finally {
      setFormButtonText(buttonInitialData);
    }
  };
  useEffect(() => {
    captureAndStoreQueryParamsBase64();
    const init = async () => {
      try {
        const campaignId = handleCampaignID();
        setTkId(campaignId);
      } catch (error: any) {
        console.error(`Error fetching event data: ${error.message}`);
      }
    };

    init();
  }, []);

  return (
    <>
      {step === 1 && (
        <div className="">
          {eventType === EventName.CAIRA &&
            formHeading === FormHeading.JOIN_US_LIVE_WITH_VARUN_JAIN && (
              <>
                <h3
                  className={`text-lg md:text-2xl font-semibold text-center tracking-[0.394px] md:tracking-[-0.48px] ${isDarkBackground ? "text-white" : "text-black"}`}
                >
                  Join us live with Varun Jain,
                </h3>
                <p
                  className={`text-sm md:text-base text-center  ${isDarkBackground ? "text-gray-300" : "text-black"}`}
                >
                  the world’s favourite CPA, CMA instructor
                </p>
              </>
            )}

          {eventType === EventName.CAIRA &&
            formHeading === FormHeading.LIVE_WEBINAR_CPA_CMA && (
              <>
                <h3
                  className={`text-lg md:text-base font-semibold text-center tracking-[0.394px] md:tracking-[-0.48px] md:leading-5 leading-5 ${isDarkBackground ? "text-white" : "text-black"}`}
                >
                  AI in Accounting 101 Webinar
                </h3>
                <p
                  className={`text-lg md:text-base font-semibold text-center tracking-[0.394px] md:tracking-[-0.48px] md:leading-5 leading-5 ${isDarkBackground ? "text-white" : "text-black"}`}
                >
                  <span className="inline-block w-2 h-2 bg-[#f33] rounded-full mr-1" />
                  <span className="text-[#f33]">Live:</span>{" "}
                  {isScheduleLoading ? (
                    <WebinarDateSkeleton width={180} />
                  ) : (
                    <>
                      <span
                        className={` ${isDarkBackground ? "text-[#FFFFFF]" : "text-[#000]"}`}
                      >
                        {weeklyDateParts.day}
                        <sup>{weeklyDateParts.suffix}</sup>{" "}
                        {weeklyDateParts.month.slice(0, 3)},{" "}
                        {weeklyWebinarEntry.day} at {weeklyWebinarEntry.time}
                      </span>
                    </>
                  )}{" "}
                </p>
              </>
            )}
          {eventType === EventName.CAIRA &&
            formHeading === FormHeading.LIVE_DATE_TIME && (
              <>
                <h3
                  className={`text-lg md:text-base font-semibold text-center tracking-[0.394px] md:tracking-[-0.48px] md:leading-5 leading-5 inline-flex items-center justify-center gap-1.5 w-full ${isDarkBackground ? "text-[#F33]" : "text-[#F33]"}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                    aria-hidden="true"
                    className="flex-shrink-0"
                  >
                    <circle cx="4" cy="4" r="4" fill="#FF3333" />
                  </svg>
                  Live:{" "}
                  <span
                    className={` ${isDarkBackground ? "text-[#FFFFFF]" : "text-[#000000]"}`}
                  >
                    {eventDate}
                  </span>{" "}
                  <span
                    className={` ${isDarkBackground ? "text-[#FFFFFF]" : "text-[#000000]"}`}
                  >
                    {eventTime}
                  </span>
                </h3>
              </>
            )}

          <form onSubmit={handleSubmit}>
            <div className="mt-5 mb-2">
              <label htmlFor="name" className="sr-only">
                Full Name
              </label>
              <input
                id="name"
                type="text"
                placeholder="Full Name*"
                value={data.name}
                name="name"
                onChange={handleInputChange}
                required
                autoComplete="off"
                className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${nameError ? "border-red-500" : isDarkBackground ? "text-[#FFF] bg-[#000] border-[#333] placeholder:text-[#777]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
              />
              {nameError && (
                <p className="text-red-500 text-xs mt-[1px] ml-1">
                  Please enter your full name
                </p>
              )}
            </div>
            <div className="mb-2">
              <label htmlFor="phone" className="sr-only">
                Phone Number
              </label>
              <PhoneInputWithCountrySelect
                autoComplete="off"
                defaultCountry="IN"
                id="phone"
                type="tel"
                value={data.phone}
                placeholder="Phone Number*"
                onChange={(value) =>
                  handlePhoneChange(value as E164Number | undefined)
                }
                flagComponent={CountryCodeDisplay}
                className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none [--PhoneInputCountryFlag-borderColor:transparent] [--PhoneInputCountryFlag-backgroundColor--loading:transparent] [--PhoneInputCountrySelectArrow-opacity:1] [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:font-medium [&_.PhoneInputInput]:text-inherit [&_.PhoneInputInput::placeholder]:opacity-100 [&_.PhoneInputCountry]:bg-transparent [&_.PhoneInputCountry]:text-inherit [&_.PhoneInputCountrySelect]:bg-transparent [&_.PhoneInputCountrySelect]:text-inherit [&_.PhoneInputCountryIcon]:!bg-transparent [&_.PhoneInputCountryIcon]:text-inherit [&_.PhoneInputCountryIcon]:!shadow-none [&_.PhoneInputCountrySelectArrow]:!opacity-100 ${
                  phoneError
                    ? "border-red-500 bg-white"
                    : isDarkBackground
                      ? "text-[#FFF] bg-[#000] border-[#333] placeholder:text-[#777] [&_.PhoneInputInput::placeholder]:!text-[#777]"
                      : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858] [&_.PhoneInputInput::placeholder]:!text-[#595858]"
                }`}
              />
              {phoneError && (
                <p className="text-red-500 text-xs mt-[1px] ml-1">
                  Please enter your number
                </p>
              )}
            </div>
            <div className="mb-2">
              <label htmlFor="email" className="sr-only">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="E-mail*"
                name="email"
                value={data.email}
                onChange={handleInputChange}
                required
                autoComplete="off"
                className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none ${emailError ? "border-red-500" : isDarkBackground ? "text-[#FFF] bg-[#000] border-[#333] placeholder:text-[#777]" : "text-[#595858] bg-white border-[#DEDEDE] placeholder:text-[#595858]"}`}
              />
              {emailError && (
                <p className="text-red-500 text-xs mt-[1px] ml-1">
                  Please enter valid email address
                </p>
              )}
            </div>
            <div className="mb-2 ml-1 flex items-center justify-start">
              <input
                id="page"
                name="whatsappAllow"
                type="checkbox"
                defaultChecked={true}
                onChange={handleInputChange}
                className={`relative w-5 h-5 md:min-w-[20px] appearance-none cursor-pointer
                  rounded-full mr-2 border-2 ${isDarkBackground ? "border-white" : "border-black"}
                  checked:bg-[#39f] checked:border-0
                  after:content-['✓'] after:absolute after:top-1/2 after:left-1/2
                  after:-translate-x-1/2 after:-translate-y-1/2 after:text-[10px]
                  after:font-bold after:text-white after:opacity-0
                  checked:after:opacity-100 after:transition-opacity after:duration-200`}
              />

              <label
                htmlFor="page"
                className={`text-[8px] md:text-[12px] font-medium tracking-[-0.373px] cursor-pointer ${isDarkBackground ? "text-[#D5D5D5]" : "text-[#002551]"}`}
              >
                I agree to receive updates via
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/whatsapp_icon.webp"
                  alt="Whatsapp logo"
                  width={12}
                  height={12}
                  className="mx-[2px] inline-block"
                />
                WhatsApp, Email & SMS.
                <a
                  href="/privacy"
                  target="_blank"
                  className={`ml-1 underline ${isDarkBackground ? "text-[#D5D5D5]" : "text-[#39F]"}`}
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            <div>
              <button
                disabled={formButtonText.disabled}
                className={`font-semibold bg-[#3399FF] text-white text-xs md:text-sm py-2 w-full capitalize rounded-[24px_24px_24px_0px] ${formButtonText.disabled ? "cursor-not-allowed opacity-50" : ""}`}
                type="submit"
              >
                {formButtonText.text}
              </button>
            </div>
          </form>
        </div>
      )}

      {step === 2 && (
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 mx-auto mb-4 text-[#1890FF]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
            />
          </svg>
          <h3
            className={`text-2xl font-bold text-center mb-2 ${isDarkBackground ? "text-white " : "text-black"} `}
          >
            Registration Successful!
          </h3>
          <h2
            className={`text-base font-normal text-center mb-4  ${isDarkBackground ? "text-white" : "text-black"}`}
          >
            Thank you for registering. We have received your details and will
            get back to you shortly with more information.
          </h2>
        </div>
      )}
    </>
  );
};

export default CairaEventForm;
